import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import React from "react";
import { toast as sonnerToast } from "sonner";

type ToastOptions = {
  durationMs?: number;
  position?: "bottom-right" | "top-center";
};

type ToastType = "success" | "error" | "warning" | "info";

const TOAST_CONFIG: Record<
  ToastType,
  {
    accent: string;
    softBg: string;
    label: string;
    icon: React.ElementType;
  }
> = {
  success: {
    accent: "#0E9F6E",
    softBg: "rgba(14, 159, 110, 0.10)",
    label: "Success",
    icon: CheckCircle2,
  },
  error: {
    accent: "#E11D48",
    softBg: "rgba(225, 29, 72, 0.10)",
    label: "Error",
    icon: XCircle,
  },
  warning: {
    accent: "#D97706",
    softBg: "rgba(217, 119, 6, 0.10)",
    label: "Warning",
    icon: AlertTriangle,
  },
  info: {
    accent: "#2563EB",
    softBg: "rgba(37, 99, 235, 0.10)",
    label: "Info",
    icon: Info,
  },
};

export const showToast = (
  message: string,
  type: ToastType = "success",
  options: ToastOptions = {}
) => {
  const { durationMs = 4000, position = "bottom-right" } = options;
  const config = TOAST_CONFIG[type];
  const Icon = config.icon;
  const isLongMessage = String(message || "").trim().length > 90;

  sonnerToast.custom(
    (toastId) =>
      React.createElement(
        "div",
        {
          className:
            "toast-enter relative flex w-auto max-w-[499px] min-w-[350px] gap-3 overflow-hidden rounded-2xl px-4 pb-4 pt-3.5",
          style: {
            background: "rgba(255, 255, 255, 0.85)",
            border: "1px solid rgba(15, 23, 42, 0.08)",
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            boxShadow: "0 20px 50px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(15, 23, 42, 0.06)",
          },
        },

        // Ambient glow behind the icon (signature element, replaces the left accent bar)
        React.createElement("div", {
          className: "pointer-events-none absolute -left-8 -top-8 h-[90px] w-[90px] rounded-full",
          style: { background: config.accent, filter: "blur(28px)", opacity: 0.22 },
        }),

        // Icon
        React.createElement(
          "div",
          {
            className:
              "toast-icon relative z-10 mt-0.5 flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px]",
            style: { backgroundColor: config.softBg },
          },
          React.createElement(Icon, { size: 17, strokeWidth: 2.4, style: { color: config.accent } })
        ),

        // Content
        React.createElement(
          "div",
          { className: "relative z-10 min-w-0 flex-1" },

          React.createElement(
            "div",
            { className: "flex items-center justify-between gap-2" },
            React.createElement(
              "span",
              {
                className: "font-mono text-[10.5px] font-semibold uppercase tracking-[0.1em]",
                style: { color: config.accent },
              },
              config.label
            ),
            React.createElement(
              "button",
              {
                type: "button",
                onClick: () => sonnerToast.dismiss(toastId),
                className:
                  "toast-close flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-900/5 hover:text-slate-700",
                "aria-label": "Close notification",
              },
              React.createElement(X, { size: 12, strokeWidth: 2.2 })
            )
          ),

          React.createElement(
            "p",
            {
              className: `mt-[5px] text-[13.5px] leading-[1.5] text-slate-700 ${
                isLongMessage ? "" : "truncate whitespace-nowrap"
              }`,
              style: isLongMessage
                ? {
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }
                : undefined,
            },
            message
          )
        ),

        // Progress bar, inset inside the card rather than pinned to the outer edge
        React.createElement(
          "div",
          {
            className: "absolute bottom-2 left-4 right-4 h-[2px] overflow-hidden rounded-full",
            style: { background: "rgba(15, 23, 42, 0.08)" },
          },
          React.createElement("div", {
            className: "toast-progress h-full rounded-full",
            style: { backgroundColor: config.accent, animationDuration: `${durationMs}ms` },
          })
        )
      ),
    { duration: durationMs, position }
  );
};
